/**
 * Empty-state line illustrations (REDESIGN §7d) — "never a bare אין נתונים".
 *
 * A 120×84 box, `fill: none`, `stroke: currentColor`, `stroke-width: 1.5`, round caps. The
 * elements marked `class="fk-illus__accent"` pick up the accent colour from `kit.css`; the rest
 * inherit the muted text colour. Module constants only — nothing here is user input, so the
 * `dangerouslySetInnerHTML` in `EmptyState.tsx` cannot become an injection point.
 *
 * No copy lives here: headings and sentences are Hebrew and belong to each app's `i18n/he.ts`.
 */

export const EMPTY_ILLUSTRATIONS = {
  /** Nothing unusual this month — a flat line and a check. */
  noAnomalies:
    '<path d="M8 58h26l7-9 7 9h20"/><path d="M74 58h38"/><circle cx="92" cy="26" r="13" class="fk-illus__accent"/><path d="M86 26.5 90.5 31 98.5 21" class="fk-illus__accent"/>',
  /** No open invoices — a document with nothing outstanding on it. */
  noInvoices:
    '<path d="M26 18v48a10 10 0 0 0 10 10h32"/><path d="M40 8h26l14 14v46a4 4 0 0 1-4 4H44a4 4 0 0 1-4-4z"/><path d="M66 8v14h14"/><path d="M50 38h20M50 48h20M50 58h12" class="fk-illus__accent"/>',
  /** No alerts — a quiet bell. */
  noAlerts:
    '<path d="M36 58c0-2.5 5-4.5 5-15a17 17 0 0 1 34 0c0 10.5 5 12.5 5 15z"/><path d="M52 62a6 6 0 0 0 12 0"/><path d="M58 22v-7"/><path d="M92 16h14l-14 14h14" class="fk-illus__accent"/>',
  /** Nothing matched this filter — a magnifier over a short list. */
  noResults:
    '<path d="M10 24h38M10 38h38M10 52h24"/><circle cx="78" cy="40" r="18" class="fk-illus__accent"/><path d="M91 53 108 70" class="fk-illus__accent"/>',
  /** The connection lapsed — a link that came apart. */
  reconnect:
    '<path d="M50 34l-9-9a15 15 0 0 0-21 21l9 9"/><path d="M70 50l9 9a15 15 0 0 0 21-21l-9-9"/><path d="M44 48l-7 7M60 32V22M76 48l7-7" class="fk-illus__accent"/>',
  /** First run, before the first sync — data on its way down. */
  firstRun:
    '<path d="M38 46a13 13 0 0 1 1-26 17 17 0 0 1 32 3 12 12 0 0 1-1 23z"/><path d="M60 38v24" class="fk-illus__accent"/><path d="M51 53l9 9 9-9" class="fk-illus__accent"/><path d="M30 72h60"/>',
} as const;

export type IllustrationName = keyof typeof EMPTY_ILLUSTRATIONS;

/** The illustration used when a name is not one we draw. */
export const FALLBACK_ILLUSTRATION: IllustrationName = 'noResults';

/** True when `name` is an illustration we actually draw. */
export function isIllustrationName(name: string): name is IllustrationName {
  return Object.prototype.hasOwnProperty.call(EMPTY_ILLUSTRATIONS, name);
}

/** The markup for an illustration, falling back to {@link FALLBACK_ILLUSTRATION}. */
export function illustrationMarkup(name: string): string {
  return isIllustrationName(name) ? EMPTY_ILLUSTRATIONS[name] : EMPTY_ILLUSTRATIONS[FALLBACK_ILLUSTRATION];
}
